/**
 * Was ein Spieler in der Aufstellung sehen und tun darf — abgeleitet, nicht entschieden.
 *
 * **Die Regel kommt aus der Engine.** Welche Felder bespielbar sind, rechnet
 * `freeStartZoneCells`: freie Felder der **eigenen** Startzone. Das ist dieselbe Menge wie beim
 * Respawn — §10 und §22 sind eine Definition und nicht zwei (`rule-context.ts`). Hier wird nichts
 * nachgerechnet, nur zusammengestellt.
 *
 * **Der Client zeigt, der Server entscheidet** (`CLAUDE.md` Regel 1). Jede Platzierung wird
 * serverseitig erneut geprüft; wäre diese Rechnung falsch, bekäme der Spieler ein
 * `ILLEGAL_ACTION` — und keine falsche Partie.
 *
 * **Der Vorrat ist der Respawn-Pool** (`ARCHITECTURE.md` **E24**). Eine ungesetzte Figur ist
 * strukturell dasselbe wie eine geschlagene; es gibt keine zweite Liste, und es soll auch keine
 * geben.
 *
 * **Fail closed:** wer nicht mitspielt, bekommt eine leere Sicht. Das sollte nicht vorkommen —
 * und wenn doch, dann ohne ein Brett, das zum Klicken einlädt.
 */

import {
  capturedOf,
  createBoard,
  createSectorLayout,
  freeStartZoneCells,
  piecesOf,
  playerOf,
  ruleContextOf,
  type CapturedPiece,
  type GameState,
  type Hex,
  type PlacedPiece,
} from '@flaghex/engine';

export interface SetupView {
  /** Die eigenen Figuren, die noch nicht stehen. */
  readonly pool: readonly CapturedPiece[];
  /** Die eigenen Figuren, die schon stehen. */
  readonly placed: readonly PlacedPiece[];
  /** Freie Felder der eigenen Startzone — leer außerhalb der Aufstellung. */
  readonly free: readonly Hex[];
  /** Hat dieser Spieler bestätigt? Aus dem Zustand gelesen (Nr. 33), nicht gemerkt. */
  readonly confirmed: boolean;
  /** Steht jede Figur? Erst dann lässt sich bestätigen (Nr. 34). */
  readonly complete: boolean;
}

const EMPTY: SetupView = {
  pool: [],
  placed: [],
  free: [],
  confirmed: false,
  complete: false,
};

export function setupOf(state: GameState, me: string): SetupView {
  if (playerOf(state, me) === undefined) {
    return EMPTY;
  }

  const pool = capturedOf(state.boardState, me);
  const placed = piecesOf(state.boardState, me);

  // Außerhalb der Aufstellung gibt es nichts zu setzen — und ein hervorgehobenes Feld wäre eine
  // Einladung zu einer Absicht, die der Server abweisen müsste.
  const free =
    state.status === 'SETUP'
      ? freeStartZoneCells(contextOf(state), me)
      : [];

  return {
    pool,
    placed,
    free,
    confirmed: state.setupConfirmed.includes(me),
    complete: pool.length === 0 && placed.length > 0,
  };
}

/**
 * Der Regelkontext dieser Stellung.
 *
 * Brett und Layout entstehen dabei neu. Das ist für einen Bildschirm vertretbar — er rechnet
 * einmal je Zustandsänderung, nicht in einer Schleife —, aber es ist der Grund, warum die
 * aufrufende Komponente das Ergebnis merken sollte.
 */
function contextOf(state: GameState) {
  const board = createBoard({ sideLength: state.ruleConfig.board.sideLength });
  return ruleContextOf(state, board, createSectorLayout(board));
}
